import { useState, useEffect, useCallback, useRef } from 'react';
import { FolderOutlined, FileImageOutlined, FileTextOutlined, FileOutlined, UploadOutlined, FolderAddOutlined } from '@ant-design/icons';
import type { ChannelFile } from '../types';
import * as api from '../lib/api';
import { toUploadFile } from '../hosts/web/files';
import { FileViewer } from './FileViewer';
import MarkdownEditor from './MarkdownEditor';

interface Props {
  channelId: string;
  readOnly?: boolean;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileIcon({ file }: { file: ChannelFile }) {
  if (file.is_directory) return <FolderOutlined />;
  if (file.mime_type?.startsWith('image/')) return <FileImageOutlined />;
  if (file.name.endsWith('.md') || file.mime_type?.startsWith('text/')) return <FileTextOutlined />;
  return <FileOutlined />;
}

export default function FilesPanel({ channelId, readOnly = false }: Props) {
  const [files, setFiles] = useState<ChannelFile[]>([]);
  const [path, setPath] = useState<{ id: string | null; name: string }[]>([{ id: null, name: 'Root' }]);
  const [selectedFile, setSelectedFile] = useState<ChannelFile | null>(null);
  const [editingFile, setEditingFile] = useState<{ file: ChannelFile; content: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; file: ChannelFile } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentParentId = path[path.length - 1]!.id;

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.listChannelFiles(channelId, currentParentId ?? undefined);
      setFiles(result);
    } catch {
      setFiles([]);
    }
    setLoading(false);
  }, [channelId, currentParentId]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  useEffect(() => {
    setPath([{ id: null, name: 'Root' }]);
    setSelectedFile(null);
    setEditingFile(null);
  }, [channelId]);

  const handleFileClick = (file: ChannelFile) => {
    if (file.is_directory) {
      setPath(prev => [...prev, { id: file.id, name: file.name }]);
    } else {
      setSelectedFile(file);
    }
  };

  const handleBreadcrumb = (index: number) => {
    setPath(prev => prev.slice(0, index + 1));
  };

  const handleUpload = async (fileList: FileList) => {
    for (const file of Array.from(fileList)) {
      await api.uploadChannelFile(channelId, await toUploadFile(file), currentParentId ?? undefined);
    }
    loadFiles();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length > 0) {
      handleUpload(e.dataTransfer.files);
    }
  };

  const handleMkdir = async () => {
    const name = prompt('Folder name:');
    if (!name?.trim()) return;
    await api.createChannelDirectory(channelId, name.trim(), currentParentId ?? undefined);
    loadFiles();
  };

  const handleDelete = async (file: ChannelFile) => {
    if (!confirm(`Delete ${file.name}?`)) return;
    await api.deleteChannelFile(channelId, file.id);
    loadFiles();
  };

  const handleRename = async (file: ChannelFile) => {
    const newName = prompt('Rename to:', file.name);
    if (!newName?.trim() || newName.trim() === file.name) return;
    try {
      await api.renameChannelFile(channelId, file.id, newName.trim());
      loadFiles();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      alert(msg.includes('409') ? 'A file with that name already exists' : 'Rename failed');
    }
  };

  const handleContextMenu = (e: React.MouseEvent, file: ChannelFile) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, file });
  };

  const handleEdit = (file: ChannelFile, content: string) => {
    setSelectedFile(null);
    setEditingFile({ file, content });
  };

  const handleSaveEdit = async (content: string) => {
    if (!editingFile) return;
    await api.updateChannelFile(channelId, editingFile.file.id, content);
    setEditingFile(null);
    loadFiles();
  };

  return (
    <div
      className="files-panel"
      onDragOver={readOnly ? undefined : (e => { e.preventDefault(); setDragging(true); })}
      onDragLeave={readOnly ? undefined : (() => setDragging(false))}
      onDrop={readOnly ? undefined : handleDrop}
      onClick={() => setContextMenu(null)}
    >
      <div className="files-panel-header">
        <h3>Files</h3>
        {!readOnly && <><button className="files-btn" onClick={handleMkdir}><FolderAddOutlined /> New Folder</button>
        <button className="files-btn" onClick={() => fileInputRef.current?.click()}><UploadOutlined /> Upload</button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={e => e.target.files && handleUpload(e.target.files)}
        /></>}
      </div>

      {path.length > 1 && (
        <div style={{ padding: '4px 12px', fontSize: '0.8em', color: 'var(--text-secondary)' }}>
          {path.map((p, i) => (
            <span key={i}>
              {i > 0 && ' / '}
              <span
                style={{ cursor: 'pointer', textDecoration: i < path.length - 1 ? 'underline' : 'none' }}
                onClick={() => handleBreadcrumb(i)}
              >
                {p.name}
              </span>
            </span>
          ))}
        </div>
      )}

      {dragging ? (
        <div className="files-drop-zone">Drop files here to upload</div>
      ) : loading ? (
        <div className="files-empty">Loading...</div>
      ) : files.length === 0 ? (
        <div className="files-empty">
          <p>No files yet</p>
          {!readOnly && <p>Upload or drag files here</p>}
        </div>
      ) : (
        <div className="file-tree">
          {files.map(file => (
            <div
              key={file.id}
              className="file-tree-item"
              onClick={() => handleFileClick(file)}
              onContextMenu={readOnly ? undefined : (e => handleContextMenu(e, file))}
            >
              <span className="file-tree-icon"><FileIcon file={file} /></span>
              <span className="file-tree-name">{file.name}</span>
              {!file.is_directory && (
                <span className="file-tree-size">{formatSize(file.size_bytes)}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {contextMenu && (
        <div className="files-context-menu" style={{ left: contextMenu.x, top: contextMenu.y }}>
          {contextMenu.file.is_directory && (
            <div className="files-context-menu-item" onClick={() => { handleFileClick(contextMenu.file); setContextMenu(null); }}>
              Open
            </div>
          )}
          <div className="files-context-menu-item" onClick={() => { handleRename(contextMenu.file); setContextMenu(null); }}>
            Rename
          </div>
          <div className="files-context-menu-item danger" onClick={() => { handleDelete(contextMenu.file); setContextMenu(null); }}>
            Delete
          </div>
        </div>
      )}

      {selectedFile && (
        <FileViewer
          file={selectedFile}
          channelId={channelId}
          onClose={() => setSelectedFile(null)}
          onEdit={!readOnly && selectedFile.name.endsWith('.md') ? handleEdit : undefined}
        />
      )}

      {editingFile && (
        <MarkdownEditor
          initialContent={editingFile.content}
          fileName={editingFile.file.name}
          onSave={handleSaveEdit}
          onCancel={() => setEditingFile(null)}
        />
      )}
    </div>
  );
}
